<script setup lang="ts">
import type { Housing } from '~/models/Housing';

const { t, locale } = useI18n();

const props = defineProps({
  housing: {
    type: Object as PropType<Housing>,
    required: true,
  },
  concise: {
    type: Boolean,
    default: false,
  },
});

const { getReadableHousingTypeById } = useHousingInfo();

const summaryElements = computed(() => {
  switch (getReadableHousingTypeById(props.housing.type.id)) {
    case 'rent-apartment':
      return [
        formatCurrency(props.housing.rent,  locale.value, { currency: props.housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        `${formatNumber(props.housing.rooms)} ${t('room_short', props.housing.rooms)}`,
        `${formatNumber(props.housing.size)}m²`,
      ];
    case 'rent-shared-flat':
      const sharedFlat: string[] = [formatCurrency(props.housing.rent,  locale.value, { currency: props.housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }), `${formatNumber(props.housing.rooms)} ${t('room_short', props.housing.rooms)}`];

      if (props.housing.flatSize) {
        sharedFlat.push(`${formatNumber(props.housing.flatSize)}m²`);
      }

      if (props.housing.flatmatesCount) {
        sharedFlat.push(`${props.housing.flatmatesCount} ${t('flatmates', props.housing.flatmatesCount)}`);
      }
      return sharedFlat;
    case 'rent-house':
      const rentHouse: string[] = [
        formatCurrency(props.housing.rent,  locale.value, { currency: props.housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        `${formatNumber(props.housing.rooms)} ${t('room_short', props.housing.rooms)}`,
        `${formatNumber(props.housing.size)}m²`,
      ];

      if (props.housing.propertySize) {
        rentHouse.push(`${formatNumber(props.housing.propertySize)}m²`);
      }

      return rentHouse;
    default:
      return [];
  }
});

const summaryOnDesktop = computed(() => {
  switch (getReadableHousingTypeById(props.housing.type.id)) {
    case 'rent-apartment':
      return [
        { key: t('cold-rent'), value: formatCurrency(props.housing.rent,  locale.value, { currency: props.housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }) },
        { key: t('room_short', props.housing.rooms), value: formatNumber(props.housing.rooms) },
        { key: t('living-space-short'), value: `${formatNumber(props.housing.size)}m²` },
      ];
    case 'rent-shared-flat':
      const sharedFlat = [
        { key: t('cold-rent'), value: formatCurrency(props.housing.rent,  locale.value, { currency: props.housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }) },
        { key: t('room-size'), value: `${formatNumber(props.housing.size)}m²` },
      ];

      if (props.housing.flatSize) {
        sharedFlat.push({ key: t('living-space'), value: `${formatNumber(props.housing.flatSize)}m²` });
      }

      if (props.housing.flatmatesCount) {
        sharedFlat.push({ key: t('shared-flat-size'), value: formatNumber(props.housing.flatmatesCount) });
      }
      return sharedFlat;
    case 'rent-house':
      const rentHouse = [
        { key: t('cold-rent'), value: formatCurrency(props.housing.rent,  locale.value, { currency: props.housing.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }) },
        { key: t('room_short', props.housing.rooms), value: formatNumber(props.housing.rooms) },
        { key: t('living-space-short'), value: `${formatNumber(props.housing.size)}m²` },
      ];

      if (props.housing.propertySize) {
        rentHouse.push({ key: t('property-space-short'), value: `${formatNumber(props.housing.propertySize)}m²` });
      }

      return rentHouse;
    default:
      return [];
  }
});
</script>

<template>
  <div>
    <template v-if="concise">
      {{ summaryElements.join(' | ') }}
    </template>
    <template v-else>
      <ul>
        <li v-for="summary in summaryOnDesktop" :key="summary.key">
          <div>{{ summary.value }}</div>
          {{ summary.key }}
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped lang="scss">
@import '~/assets/styles/tw-mixins.scss';

ul {
  gap: 1rem;
  padding: 0;
  display: grid;
  list-style: none;
  overflow-wrap: break-word;
  grid-template-columns: repeat(auto-fit, minmax(1rem, 1fr));

  li {
    div {
      font-weight: bold;
    }
  }
}
</style>
